import GrowIcon from "../../icons/harmonization/GrowIcon"
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputBase,
  Paper,
  Typography,
} from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import { useEffect, useState } from "react"
import { clearDataSets, getScenarioList } from "./HarmonizationSlice"
import { API_STATUS, SCREENS_DETAILS } from "../../util/StaticData"
import ScenarioTable from "./ScenarioTable"
import { useNavigate } from "react-router-dom"
import { IoSearch } from "react-icons/io5"
import { setShowMappingSummary } from "./mapping/MapSlice"

const HarmonizationScenarioList = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState("")
  const { scenarioList, isScenarioListLoading } = useAppSelector(
    (state) => state.harmonization,
  )

  useEffect(() => {
    dispatch(getScenarioList())
  }, [dispatch])

  const handleCreateNewScenario = () => {
    dispatch(clearDataSets())
    dispatch(setShowMappingSummary(false))
    navigate(SCREENS_DETAILS.CREATE_SCENARIO)
  }

  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value)
  }

  const filteredScenarioList = scenarioList
    .filter((scenario) => {
      return (
        scenario.sourceDataset
          .toLowerCase()
          .includes(searchValue.toLowerCase()) ||
        scenario.targetDataset.toLowerCase().includes(searchValue.toLowerCase())
      )
    })
    .sort((a, b) => {
      const aMatch =
        a.sourceDataset.toLowerCase().includes(searchValue.toLowerCase()) ||
        a.targetDataset.toLowerCase().includes(searchValue.toLowerCase())
      const bMatch =
        b.sourceDataset.toLowerCase().includes(searchValue.toLowerCase()) ||
        b.targetDataset.toLowerCase().includes(searchValue.toLowerCase())

      if (aMatch && !bMatch) return -1
      if (!aMatch && bMatch) return 1

      const aScore =
        a.sourceDataset.toLowerCase().indexOf(searchValue.toLowerCase()) +
        a.targetDataset.toLowerCase().indexOf(searchValue.toLowerCase())
      const bScore =
        b.sourceDataset.toLowerCase().indexOf(searchValue.toLowerCase()) +
        b.targetDataset.toLowerCase().indexOf(searchValue.toLowerCase())

      return aScore - bScore
    })

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        borderRadius: 2,
        padding: 3,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GrowIcon />
          <Typography
            variant="tableHeaderMaintenance"
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Dataset Harmonization Scenario List
          </Typography>
        </Box>

        <Button
          variant="contained"
          sx={{
            fontSize: "12px",
            borderRadius: "20px",
            backgroundColor: "primary.main",
            color: "background.paper",
            ":hover": { backgroundColor: "customColors.hoverPrimary" },
            fontWeight: 600,
            px: 5,
            boxShadow: 0,
            textTransform: "none",
          }}
          onClick={handleCreateNewScenario}
        >
          Create New Scenario
        </Button>
      </Box>

      <Divider />

      <Box
        sx={{ display: "flex", flexGrow: 1, flexDirection: "column", gap: 3 }}
      >
        <Paper
          component="form"
          sx={{
            display: "flex",
            alignItems: "center",
            width: 314,
            padding: "2px 8px",
            boxShadow: 0,
            alignSelf: "flex-end",
            border: (theme) => `1px solid ${theme.palette.borderColors[50]}`,
          }}
        >
          <IoSearch color={"#0B0E1E4D"} />
          <InputBase
            value={searchValue}
            onChange={handleSearchValueChange}
            sx={{ flex: 1, ml: 1 }}
            placeholder="Search Dataset"
            inputProps={{ "aria-label": "search dataset" }}
          />
        </Paper>

        {isScenarioListLoading === API_STATUS.LOADING ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "10rem",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              scrollbarWidth: "thin",
            }}
            className="flex-1 grid grid-cols-1 gap-4 custom-scrollbar"
          >
            <ScenarioTable data={filteredScenarioList} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default HarmonizationScenarioList
